import { collection, query, getDocs, where, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { LevelAnalytics, DailySolves, LevelAttempt, AnalyticsSummary, AnalyticsEvent, EventFilters, MonetizationMetrics } from '../types/Analytics';

const ANALYTICS_COLLECTION = 'events';

export const analyticsService = {
  /**
   * Get analytics for a specific level
   */
  async getLevelAnalytics(levelId: string): Promise<LevelAnalytics> {
    try {
      const attemptsRef = collection(db, ANALYTICS_COLLECTION);
      const q = query(attemptsRef, where('levelId', '==', levelId));
      const snapshot = await getDocs(q);

      let solves = 0;
      let fails = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.success) {
          solves++;
        } else {
          fails++;
        }
      });

      const successRate = solves + fails > 0 ? (solves / (solves + fails)) * 100 : 0;

      return {
        levelId,
        solves,
        fails,
        successRate,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching level analytics:', error);
      return {
        levelId,
        solves: 0,
        fails: 0,
        successRate: 0,
        lastUpdated: new Date().toISOString()
      };
    }
  },

  /**
   * Get analytics for all levels
   */
  async getAllLevelAnalytics(days: number = 3650): Promise<Map<string, LevelAnalytics>> {
    try {
      const attemptsRef = collection(db, ANALYTICS_COLLECTION);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const q = days >= 3650
        ? query(attemptsRef)
        : query(attemptsRef, where('timestamp', '>=', Timestamp.fromDate(cutoffDate)));
      const snapshot = await getDocs(q);

      const levelStatsMap = new Map<string, { solves: number; fails: number }>();

      snapshot.forEach(doc => {
        const data = doc.data();
        const levelId = data.levelId;

        if (!levelStatsMap.has(levelId)) {
          levelStatsMap.set(levelId, { solves: 0, fails: 0 });
        }

        const stats = levelStatsMap.get(levelId)!;
        if (data.success) {
          stats.solves++;
        } else {
          stats.fails++;
        }
      });

      const analyticsMap = new Map<string, LevelAnalytics>();
      levelStatsMap.forEach((stats, levelId) => {
        const successRate = stats.solves + stats.fails > 0
          ? (stats.solves / (stats.solves + stats.fails)) * 100
          : 0;

        analyticsMap.set(levelId, {
          levelId,
          solves: stats.solves,
          fails: stats.fails,
          successRate,
          lastUpdated: new Date().toISOString()
        });
      });

      return analyticsMap;
    } catch (error) {
      console.error('Error fetching all level analytics:', error);
      return new Map();
    }
  },

  /**
   * Get daily solves for the last N days
   */
  async getDailySolves(days: number = 30): Promise<DailySolves[]> {
    try {
      const attemptsRef = collection(db, ANALYTICS_COLLECTION);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      const snapshot = await getDocs(attemptsRef);

      const dailyMap = new Map<string, { solves: number; fails: number }>();

      snapshot.forEach(doc => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate?.() || new Date(data.timestamp);

        if (timestamp >= cutoffDate) {
          const dateKey = timestamp.toISOString().split('T')[0];

          if (!dailyMap.has(dateKey)) {
            dailyMap.set(dateKey, { solves: 0, fails: 0 });
          }

          const stats = dailyMap.get(dateKey)!;
          if (data.success) {
            stats.solves++;
          } else {
            stats.fails++;
          }
        }
      });

      const dailySolves: DailySolves[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];

        const stats = dailyMap.get(dateKey) || { solves: 0, fails: 0 };
        dailySolves.push({
          date: dateKey,
          solves: stats.solves,
          fails: stats.fails
        });
      }

      return dailySolves;
    } catch (error) {
      console.error('Error fetching daily solves:', error);
      return [];
    }
  },

  /**
   * Get comprehensive analytics summary
   */
  async getAnalyticsSummary(days: number = 30): Promise<AnalyticsSummary> {
    try {
      const levelAnalytics = await this.getAllLevelAnalytics(days);
      const dailySolves = await this.getDailySolves(days);

      // Fetch all events for the timeline graph
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const startDate = days >= 3650 ? undefined : cutoffDate;
      const recentEvents = await this.getAllEvents(2000, startDate ? { startDate } : {});

      let totalSolves = 0;
      let totalFails = 0;
      let mostDifficultLevel: string | null = null;
      let easiestLevel: string | null = null;
      let lowestSuccessRate = 100;
      let highestSuccessRate = 0;

      levelAnalytics.forEach((stats, levelId) => {
        totalSolves += stats.solves;
        totalFails += stats.fails;

        if (stats.solves + stats.fails > 5) { // Only consider levels with enough attempts
          if (stats.successRate < lowestSuccessRate) {
            lowestSuccessRate = stats.successRate;
            mostDifficultLevel = levelId;
          }
          if (stats.successRate > highestSuccessRate) {
            highestSuccessRate = stats.successRate;
            easiestLevel = levelId;
          }
        }
      });

      const totalAttempts = totalSolves + totalFails;
      const averageSuccessRate = totalAttempts > 0 ? (totalSolves / totalAttempts) * 100 : 0;

      // Get unique players count within the selected time range
      const attemptsRef = collection(db, ANALYTICS_COLLECTION);
      const playersQ = startDate
        ? query(attemptsRef, where('timestamp', '>=', Timestamp.fromDate(startDate)))
        : query(attemptsRef);
      const snapshot = await getDocs(playersQ);
      const uniqueUsers = new Set<string>();
      const newUserIds = new Set<string>(); // users who created their account in this range
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.userId) {
          uniqueUsers.add(data.userId);
          if (data.eventType === 'user_created') {
            newUserIds.add(data.userId);
          }
        }
      });
      const newUsers = newUserIds.size;
      const returningUsers = uniqueUsers.size - newUsers;

      return {
        totalPlayers: uniqueUsers.size,
        totalAttempts,
        totalSolves,
        totalFails,
        averageSuccessRate,
        newUsers,
        returningUsers,
        mostDifficultLevel,
        easiestLevel,
        dailySolves,
        levelAnalytics,
        recentEvents
      };
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      return {
        totalPlayers: 0,
        totalAttempts: 0,
        totalSolves: 0,
        totalFails: 0,
        averageSuccessRate: 0,
        newUsers: 0,
        returningUsers: 0,
        mostDifficultLevel: null,
        easiestLevel: null,
        dailySolves: [],
        levelAnalytics: new Map(),
        recentEvents: []
      };
    }
  },

  /**
   * Record a level attempt (called from Unity game)
   */
  async recordLevelAttempt(attempt: Omit<LevelAttempt, 'timestamp'>): Promise<boolean> {
    try {
      const attemptsRef = collection(db, ANALYTICS_COLLECTION);
      await addDoc(attemptsRef, {
        ...attempt,
        timestamp: Timestamp.now()
      });
      return true;
    } catch (error) {
      console.error('Error recording level attempt:', error);
      return false;
    }
  },

  /**
   * Get all analytics events with optional filtering
   */
  async getAllEvents(limit: number = 100, filters?: EventFilters): Promise<AnalyticsEvent[]> {
    try {
      const eventsRef = collection(db, ANALYTICS_COLLECTION);
      let q = query(eventsRef);

      // Apply filters
      if (filters?.eventType) {
        q = query(q, where('eventType', '==', filters.eventType));
      }
      if (filters?.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }
      if (filters?.startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(filters.startDate)));
      }
      if (filters?.endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(filters.endDate)));
      }

      const snapshot = await getDocs(q);
      const events: AnalyticsEvent[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        events.push({
          id: doc.id,
          userId: data.userId || 'unknown',
          userName: data.userName,
          eventType: data.eventType || 'level_complete',
          timestamp: data.timestamp?.toDate() || new Date(),
          metadata: data.metadata || {}
        });
      });

      // Sort by timestamp descending and limit
      return events
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  },

  /**
   * Get monetization metrics
   */
  async getMonetizationMetrics(days: number = 3650): Promise<MonetizationMetrics> {
    try {
      const eventsRef = collection(db, ANALYTICS_COLLECTION);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const q = query(eventsRef, where('timestamp', '>=', Timestamp.fromDate(cutoffDate)));
      const snapshot = await getDocs(q);

      let totalRevenue = 0;
      let totalPurchases = 0;
      let totalAdViews = 0;
      const productMap = new Map<string, { itemName: string; purchases: number; revenue: number }>();
      const uniqueUsers = new Set<string>();

      snapshot.forEach(doc => {
        const data = doc.data();
        const eventType = data.eventType;

        if (data.userId) {
          uniqueUsers.add(data.userId);
        }

        // IAP purchases
        if (eventType === 'iap_purchase' && data.metadata) {
          const price = data.metadata.priceUSD || 0;
          totalRevenue += price;
          totalPurchases++;

          const productId = data.metadata.productId;
          const itemName = data.metadata.itemName || productId;

          if (productId) {
            if (!productMap.has(productId)) {
              productMap.set(productId, { itemName, purchases: 0, revenue: 0 });
            }
            const product = productMap.get(productId)!;
            product.purchases++;
            product.revenue += price;
          }
        }

        // Ad views
        if (eventType === 'ad_watched' || data.eventType === 'ad_reward') {
          totalAdViews++;
        }
      });

      const totalPlayers = uniqueUsers.size;
      const avgRevenuePerUser = totalPlayers > 0 ? totalRevenue / totalPlayers : 0;
      const conversionRate = totalPlayers > 0 ? (totalPurchases / totalPlayers) * 100 : 0;

      // Convert product map to sorted array
      const topProducts = Array.from(productMap.entries())
        .map(([productId, data]) => ({
          productId,
          itemName: data.itemName,
          purchases: data.purchases,
          revenue: data.revenue
        }))
        .sort((a, b) => b.revenue - a.revenue);

      return {
        totalRevenue,
        totalPurchases,
        totalAdViews,
        avgRevenuePerUser,
        conversionRate,
        topProducts
      };
    } catch (error) {
      console.error('Error fetching monetization metrics:', error);
      return {
        totalRevenue: 0,
        totalPurchases: 0,
        totalAdViews: 0,
        avgRevenuePerUser: 0,
        conversionRate: 0,
        topProducts: []
      };
    }
  }
};
